import { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { DashboardIcon } from '../components/Icons';
import { fetchPortfolio, fetchPrice, detectCurrency } from '../utils/api';
import { useCurrency } from '../utils/currency';

function Dashboard() {
  const [portfolio, setPortfolio] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ value: 0, cost: 0, gain: 0, pct: 0, altValue: 0, altCost: 0, altGain: 0 });
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { displayCurrency, altCurrency, convert, convertToAlt, rateLoaded } = useCurrency();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (rawData.length > 0 && rateLoaded) {
      recalculate(rawData);
    }
  }, [displayCurrency, rateLoaded]);

  async function loadData() {
    setLoading(true);
    const holdings = await fetchPortfolio();
    setPortfolio(holdings);
    if (holdings.length === 0) { setLoading(false); return; }

    const results = [];
    for (const h of holdings) {
      const priceData = await fetchPrice(h.ticker);
      const price = priceData ? priceData.price : h.buy_price;
      const nativeCurrency = detectCurrency(h.ticker);
      const storedCurrency = (h.currency || nativeCurrency).toUpperCase();

      results.push({
        ticker: h.ticker,
        shares: h.shares,
        buyPriceRaw: h.buy_price,
        buyPriceCurrency: storedCurrency,
        currentPrice: price,
        currentPriceCurrency: nativeCurrency,
        broker: h.broker,
      });
    }

    setRawData(results);
    recalculate(results);
    setLoading(false);
  }

  function recalculate(data) {
    let totalValue = 0, totalCost = 0;
    let totalAltValue = 0, totalAltCost = 0;

    const computed = data.map(r => {
      const currentValue = r.currentPrice * r.shares;
      const costBasis = r.buyPriceRaw * r.shares;

      const displayBuyPrice = convert(r.buyPriceRaw, r.buyPriceCurrency);
      const displayCurrentPrice = convert(r.currentPrice, r.currentPriceCurrency);
      const displayValue = displayCurrentPrice * r.shares;
      const displayCost = displayBuyPrice * r.shares;

      const altBuyPrice = convertToAlt(r.buyPriceRaw, r.buyPriceCurrency);
      const altCurrentPrice = convertToAlt(r.currentPrice, r.currentPriceCurrency);
      const altValue = altCurrentPrice * r.shares;
      const altCost = altBuyPrice * r.shares;

      const gainPct = displayBuyPrice ? ((displayCurrentPrice - displayBuyPrice) / displayBuyPrice * 100) : 0;

      totalValue += displayValue;
      totalCost += displayCost;
      totalAltValue += altValue;
      totalAltCost += altCost;

      return {
        ticker: r.ticker,
        shares: r.shares,
        buyPrice: displayBuyPrice,
        currentPrice: displayCurrentPrice,
        currentValue: displayValue,
        gainPct,
        altValue,
      };
    });

    const totalGain = totalValue - totalCost;
    const totalPct = totalCost ? (totalGain / totalCost * 100) : 0;
    const totalAltGain = totalAltValue - totalAltCost;
    setRows(computed);
    setTotals({
      value: totalValue,
      cost: totalCost,
      gain: totalGain,
      pct: totalPct,
      altValue: totalAltValue,
      altCost: totalAltCost,
      altGain: totalAltGain,
    });
  }

  if (loading) {
    return (
      <div>
        <div className="page-title">
          <DashboardIcon size={28} color="#00ff9d" />
          <h1>Dashboard</h1>
        </div>
        <p className="hero-subtitle">Your portfolio at a glance — live prices from NASDAQ & TSX</p>
        <div className="loading-text"><span className="spinner" />Fetching live prices...</div>
      </div>
    );
  }

  if (portfolio.length === 0) {
    return (
      <div>
        <div className="page-title">
          <DashboardIcon size={28} color="#00ff9d" />
          <h1>Dashboard</h1>
        </div>
        <p className="hero-subtitle">Your portfolio at a glance — live prices from NASDAQ & TSX</p>
        <div className="glass-card empty-state">
          <h3>Welcome to Retail Alpha</h3>
          <p>Head over to <strong>Portfolio Manager</strong> to add your first holding and unlock your dashboard.</p>
        </div>
      </div>
    );
  }

  const sign = totals.gain >= 0 ? '+' : '';
  const altSign = totals.altGain >= 0 ? '+' : '';
  const colors = ['#00ff9d', '#00ccff', '#a855f7', '#f97316', '#ec4899', '#06b6d4', '#84cc16', '#eab308', '#f43f5e', '#6366f1'];
  const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="page-title">
        <DashboardIcon size={28} color="#00ff9d" />
        <h1>Dashboard</h1>
      </div>
      <p className="hero-subtitle">Your portfolio at a glance — live prices from NASDAQ & TSX</p>

      <div className="metrics-row">
        <div className="metric-card">
          <div className="metric-label">Total Portfolio Value ({displayCurrency})</div>
          <div className="metric-value">${fmt(totals.value)}</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{altCurrency} ${fmt(totals.altValue)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total Gain / Loss ({displayCurrency})</div>
          <div className="metric-value">{sign}${fmt(totals.gain)}</div>
          <div className={`metric-delta ${totals.gain >= 0 ? 'gain' : 'loss'}`}>{sign}{totals.pct.toFixed(2)}%</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{altCurrency} {altSign}${fmt(totals.altGain)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Number of Holdings</div>
          <div className="metric-value">{portfolio.length}</div>
        </div>
      </div>

      <hr />

      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12 }}>Asset Allocation</h3>
        <div style={{ display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
          <Plot
            data={[{
              type: 'pie',
              labels: rows.map(r => r.ticker),
              values: rows.map(r => r.currentValue),
              hole: 0.45,
              marker: {
                colors: colors.slice(0, rows.length),
                line: { color: '#0a0f1c', width: 2 },
              },
              textfont: { color: 'white', size: 12 },
              hovertemplate: '<b>%{label}</b><br>Value: %{value:,.2f}<br>Share: %{percent}<extra></extra>',
            }]}
            layout={{
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              font: { color: '#94a3b8', family: 'Inter, sans-serif' },
              legend: { font: { color: '#cbd5e1', size: 12 }, bgcolor: 'rgba(0,0,0,0)', orientation: 'h', x: 0.5, xanchor: 'center', y: -0.05 },
              margin: { t: 10, b: 40, l: 10, r: 10 },
              height: 340,
              showlegend: true,
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%', maxWidth: 500 }}
          />
        </div>
      </div>

      <div>
        <h3 style={{ marginBottom: 12 }}>Holdings Detail</h3>
        <div className="glass-card" style={{ padding: 0, overflow: 'auto' }}>
          <table className="data-table holdings-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Shares</th>
                <th>Buy Price</th>
                <th>Current</th>
                <th className="hide-mobile">Currency</th>
                <th className="hide-mobile">Value</th>
                <th>Gain/Loss</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.ticker}>
                  <td style={{ color: '#e2e8f0', fontWeight: 600 }}>{r.ticker}</td>
                  <td>{r.shares}</td>
                  <td>{r.buyPrice.toFixed(2)}</td>
                  <td>{r.currentPrice.toFixed(2)}</td>
                  <td className="hide-mobile">{displayCurrency}</td>
                  <td className="hide-mobile">{fmt(r.currentValue)}</td>
                  <td className={r.gainPct >= 0 ? 'gain' : 'loss'}>{r.gainPct >= 0 ? '+' : ''}{r.gainPct.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
